import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setUnauthorizedHandler } from './lib/api';
import { useSession } from './store/session';
import { Layout } from './components/Layout';
import { SetupPage } from './pages/Setup';
import { LoginPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { WalletPage } from './pages/Wallet';
import { ReceivePage } from './pages/Receive';
import { SendPage } from './pages/Send';
import { ChainsPage } from './pages/Chains';
import { ActivityPage } from './pages/Activity';
import { TestingPage } from './pages/Testing';
import { FamilyPage } from './pages/Family';
import { SpendPage } from './pages/Spend';
import { AddFundsPage } from './pages/AddFunds';
import { SettingsPage } from './pages/Settings';
import { RequestsPage } from './pages/Requests';
import { PublicRequestPage } from './pages/PublicRequest';
import { DocsPage } from './pages/Docs';
import { Loader2 } from 'lucide-react';

export function App() {
  const status = useSession((s) => s.status);
  const isAdmin = useSession((s) => s.isAdmin);
  const bootstrap = useSession((s) => s.bootstrap);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // One place decides what happens when the server rejects our session.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      useSession.setState({ status: 'unauthenticated', username: null, userId: null });
      navigate('/login', { replace: true });
    });
  }, [navigate]);

  // Public pages — no account needed. Payment request links and the API
  // docs render before any auth gate.
  if (location.pathname.startsWith('/r/') || location.pathname === '/docs') {
    return (
      <Routes>
        <Route path="/r/:slug" element={<PublicRequestPage />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-void">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="animate-spin text-brass" size={22} />
          <p className="text-sm text-ink-faint">Starting Gauntlet…</p>
        </div>
      </div>
    );
  }

  if (status === 'setup-required') {
    return (
      <Routes>
        <Route path="*" element={<SetupPage />} />
      </Routes>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/wallets/:walletId" element={<WalletPage />} />
        <Route path="/accounts/:accountId/receive" element={<ReceivePage />} />
        <Route path="/accounts/:accountId/send" element={<SendPage />} />
        <Route path="/activity" element={<ActivityPage />} />
        {isAdmin && <Route path="/testing" element={<TestingPage />} />}
        <Route path="/requests" element={<RequestsPage />} />
        <Route path="/family" element={<FamilyPage />} />
        <Route path="/spend" element={<SpendPage />} />
        <Route path="/add-funds" element={<AddFundsPage />} />
        <Route path="/chains" element={<ChainsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
